import { fromJS, OrderedMap } from 'immutable';
import actions from 'react/api/actions';
import { EXTERNAL_LINK_MENUITEMS } from 'react/api/constants/tabs';

const {
  updateTitle,
  onResize,
  onSort,
  onClose,
  openPanelAtLogin,
  openHelp,
  openHelpAfterLogin,
  openRouteSuggestion,
  setExtraArgs
} = actions.tabs.actions;

const initialState = fromJS({
  showNav: false,
  list: new OrderedMap(),
  active: {}
});

const sortList = (source, target) => (list) => {
  const sourceMap = list.get(source.liveKey);
  return list.remove(source.liveKey)
    .reduce((newList, item) => {
      if (item.get('key') !== target.liveKey) {
        return newList.mergeDeepIn([], {
          [item.get('key')]: item
        });
      }
      return newList.mergeDeepIn([], {
        [sourceMap.get('key')]: sourceMap,
        [item.get('key')]: item
      });
    }, new OrderedMap());
};

const formatTabForDataImport = (pathname, state) => {
  const [action, processId, encodedFileName] = pathname
    .split('/').slice(2, 5);
  if (action === 'SeeLogs' && processId && encodedFileName) {
    const fileName = decodeURI(encodedFileName);
    const seeLogKey = `SeeLogs_${processId}`;
    const exist = state.getIn(['list', seeLogKey]);
    const title = exist ? exist.get('title') : seeLogKey;
    const tab = fromJS({
      key: seeLogKey,
      title,
      closeable: true,
      pathname,
      tag: 'SeeLogs',
      data: {
        processId,
        fileName
      },
      extraTabParam: fileName
    });

    return { tab, seeLogKey };
  }
  return {};
};

const getDataFromInheritedRouters = (pathname) => {
  const [parentId, router, mode, id] = pathname.split('/').slice(2);

  if (router && router.match(/^(HoursOfServiceTypeDays|TimeWindowTypeDays|LocationOverrideTimeWindow)$/)) {
    return { extra: { parentId, mode, id }, key: router };
  }

  return null;
};

const getDataFromRouteDetails = (pathname) => {
  const [routeId, details, mode, childrenId] = pathname.split('/').slice(3, 7);

  return {
    extra: {
      routeId, details, mode, childrenId
    },
    key: `RouteDetails_${routeId}`,
    tag: 'RouteDetails'
  };
};

const getDataFromTicketDetails = (pathname) => {
  const [idString] = pathname.split('/').slice(3, 4);
  const id = parseInt(idString, 10);

  return {
    extra: {
      id
    },
    key: `TicketDetails_${id}`,
    tag: 'TicketDetails'
  };
};

const getDataFromIntegrationLogs = (pathname) => {
  const [id] = pathname.split('/').slice(3, 4);

  return {
    extra: {
      id
    },
    key: `IntegrationLog_${id}`,
    tag: 'IntegrationLog'
  };
};

const getDataFromLocalzLogs = () => {
  return {
    key: 'LocalzIntegrationLog',
    tag: 'LocalzIntegrationLog'
  };
};

const getDataFromRetriedLogs = (pathname) => {
  const [id] = pathname.split('/').slice(3, 4);

  return {
    extra: {
      id
    },
    key: `RetriedLogs_${id}`,
    tag: 'RetriedLogs'
  };
};

const getDataFromWGLNIntegrationLogs = () => {
  return {
    key: 'WGLNIntegrationLog',
    tag: 'WGLNIntegrationLog'
  };
};

const formatTabObject = (key, pathname, state, originalKey) => {
  let tab = {
    key,
    title: '',
    closeable: true,
    pathname,
    tag: '',
    extra: null,
    extraArgs: state.getIn(['active', 'extraArgs'])
  };

  switch (originalKey) {
    case 'Panel': {
      const id = parseInt(pathname.split('/').slice(2), 10);
      tab = {
        ...tab, extra: { id }, key: `Panel_${id}`, tag: 'Panel'
      };
      break;
    }
    case 'HoursOfServiceType':
    case 'TimeWindowType':
    case 'Location': {
      tab = {
        ...tab, ...getDataFromInheritedRouters(pathname)
      };
      break;
    }
    case 'maps': {
      tab = {
        ...tab, ...getDataFromRouteDetails(pathname)
      };
      break;
    }
    case 'ticket': {
      if (pathname.split('/').slice(1, 3).join('/') === 'ticket/details') {
        tab = {
          ...tab, ...getDataFromTicketDetails(pathname)
        };
      }
      break;
    }
    case 'Integrations': {
      const pathToTest = pathname.split('/').slice(1, 3).join('/');

      const pathToIntegrationTabData = {
        'Integrations/wgln': getDataFromWGLNIntegrationLogs(),
        'Integrations/localz': getDataFromLocalzLogs(),
        'Integrations/logs': getDataFromIntegrationLogs(pathname),
        'Integrations/retriedLogs': getDataFromRetriedLogs(pathname),
        'none': null
      };

      const integrationTabData = pathToIntegrationTabData[pathToTest ?? 'none'];

      tab = {
        ...tab,
        ...integrationTabData
      };
      break;
    }

    default:
      break;
  }
  const title = state
    .getIn(['list', tab.key], fromJS({}))
    .get('title', tab.key);
  const tabMapped = fromJS({ ...tab, title });

  return state.set('active', tabMapped).mergeDeepIn(['list'], { [tab.key]: tabMapped });
};

const formatTabOnLocationChange = (payload, state) => {
  const { pathname } = payload.location;
  const [originalKey] = pathname.split('/').slice(1);

  if (originalKey in EXTERNAL_LINK_MENUITEMS) {
    return state;
  }

  if (originalKey === 'DataImport') {
    const { tab, seeLogKey } = formatTabForDataImport(pathname, state);
    if (tab && seeLogKey) {
      return state.set('active', tab).mergeDeepIn(['list'], { [seeLogKey]: tab });
    }
  }
  const key = originalKey === 'welcome' ? 'RouteMonitor' : originalKey;
  if (key && key !== 'login') {
    return formatTabObject(key, pathname, state, originalKey);
  }
  return state;
};

export default (state = initialState, { type, payload, settings }) => {
  if (!payload) return state;
  switch (type) {
    case openHelp: {
      const { key, title } = payload;
      const tab = fromJS({
        key,
        title,
        closeable: true,
        pathname: `${payload.key}`,
        tag: 'Help',
        extra: settings
      });

      return state.set('active', tab).mergeDeepIn(['list'], { [tab.get('key')]: tab });
    }
    case openHelpAfterLogin: {
      const { key, title } = payload;
      const tab = fromJS({
        key,
        title,
        closeable: true,
        pathname: `${payload.key}`,
        tag: 'Help',
        extra: settings
      });
      return state.mergeDeepIn(['list'], { [key]: tab });
    }
    case openPanelAtLogin: {
      if (payload) {
        const list = payload
          .filter((panel) => (panel.openAtLogin))
          .map((tab) => (
            {
              key: `Panel_${tab.id}`,
              title: tab.name,
              closeable: true,
              pathname: `/Panel/${tab.id}`,
              tag: 'Panel',
              extra: tab
            }
          )).reduce((acc, tab) => ({
            ...acc,
            [tab.key]: fromJS(tab)
          }), {});

        return state.mergeDeepIn(['list'], list);
      }

      return state;
    }
    case openRouteSuggestion: {
      const { key, title } = payload;
      const tab = fromJS({
        key,
        title,
        closeable: true,
        pathname: `${payload.key}`,
        tag: 'RouteSuggestion'
      });

      return state.set('active', tab).mergeDeepIn(['list'], { [key]: tab });
    }
    case updateTitle: {
      const findTab = state.get('list').find((item) => {
        const key = (item.get) ? item.get('key') : item.key;
        return key === payload.key;
      });

      if (!findTab) return state;

      return state.mergeDeepIn(['list'], { [payload.key]: payload });
    }
    case onResize:
      return state.set('showNav', payload.showNav);
    case onSort: {
      const { source, target } = payload;
      if (source.liveKey !== target.liveKey) {
        return state.update('list', sortList(source, target));
      }
      return state;
    }
    case onClose:
      return state.removeIn(['list', payload.get('key')]);
    case setExtraArgs:
      return state.setIn(['active', 'extraArgs'], payload);
    case '@@router/LOCATION_CHANGE': {
      if (payload.action === 'POP' || payload.action === 'REPLACE' || payload.action === 'PUSH') {
        return formatTabOnLocationChange(payload, state);
      }
      return state;
    }
    default:
      break;
  }

  return state;
};
